import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ROUTE_MESSENGER_SUPPORT } from 'navigation/config/routesConfig';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { useConversations } from 'providers/ConversationProvider';
import { useMessages } from 'providers/MessageProvider';
import { generateTmpMessageId } from 'providers/utils/conversationProviderUtils';
import { ConversationDto, ConversationWithLastMessageDto } from 'services/message/messageService.dto';

import { MessengerState } from './messengerState';

export const useConversationSelect = () => {
  const { conversationId, userId, itemId, campaignId } = useParams();
  const { state, pathname }: { state: MessengerState; pathname: string } = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const [initialConversationId, setInitialConversationId] = useState<number>();
  const [initialConversationResolved, setInitialConversationResolved] = useState(false);

  const [selectedConversation, setSelectedConversation] = useState<ConversationDto>();
  const [currentConversations, setCurrentConversations] = useState<ConversationWithLastMessageDto[]>([]);

  const { getConversationMessages } = useMessages();
  const {
    conversations,
    createConversation,
    resetConversations,
    fetchConversationMessages,
    setCurrentConversationId,
    currentConversationId,
    fetchConversations,
    fetchConversation,
    addTextMessage,
  } = useConversations();

  const conversationMessages = !!selectedConversation
    ? getConversationMessages(selectedConversation.conversationId)
    : [];

  const setConversationId = (id: number) => {
    const conversation = conversations.find(conv => conv.conversation.conversationId === id);
    setSelectedConversation(conversation?.conversation);
  };

  useEffect(() => {
    resetConversations();
  }, []);

  useEffect(() => {
    if (!conversations.length) {
      fetchConversations();
    }
  }, [!!conversations.length]);

  useEffect(() => {
    if (!!conversations.length && !!currentUser) {
      resolveInitialConversationId();
    }
  }, [!!conversations.length, !!currentUser]);

  useEffect(() => {
    if (!!conversations.length && initialConversationResolved) {
      processConversations();
    }
  }, [conversations, initialConversationResolved]);

  useEffect(() => {
    if (!!selectedConversation) {
      fetchConversationMessages(selectedConversation.conversationId);
      setCurrentConversationId(selectedConversation.conversationId);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (!currentConversationId) {
      setSelectedConversation(undefined);
    }
  }, [currentConversationId]);

  const processConversations = async () => {
    const initialConversation = conversations.find(it => it.conversation.conversationId === initialConversationId);

    if (!!initialConversation) {
      const otherConversations = conversations.filter(it => it.conversation.conversationId !== initialConversationId);
      setCurrentConversations([initialConversation, ...otherConversations]);

      if (!selectedConversation) {
        setSelectedConversation(initialConversation.conversation);

        if (initialConversation.conversation.context === 'SUPPORT') {
          addInitialMessage(initialConversation.conversation.conversationId);
        }
      }
    } else {
      setCurrentConversations(conversations);
    }
  };

  const resolveInitialConversationId = async () => {
    if (!initialConversationResolved) {
      const initialConversationId = await findInitialConversationId();
      setInitialConversationId(initialConversationId);
      setInitialConversationResolved(true);
      navigate('/messenger', { replace: true, state });
    }
  };

  const findInitialConversationId = async () => {
    if (Number(userId) === currentUser?.id) {
      return; // user cannot create conversation to himself
    }

    if (!!userId && !!itemId) {
      const conversation = conversations.find(({ conversation }) => {
        const { context, item, interlocutors } = conversation;
        const isInterlocutor = interlocutors.some(interlocutor => interlocutor.id === Number(userId));
        return isInterlocutor && context === 'ITEM' && item?.id === Number(itemId);
      });

      if (!!conversation) {
        return conversation.conversation.conversationId;
      } else {
        return await createConversation({ context: 'ITEM', contextualObjectId: Number(itemId) }, Number(userId));
      }
    } else if (!!userId && !!campaignId) {
      const conversation = conversations.find(({ conversation }) => {
        const { context, campaign, interlocutors } = conversation;
        const isInterlocutor = interlocutors.some(interlocutor => interlocutor.id === Number(userId));
        return isInterlocutor && context === 'CAMPAIGN' && campaign?.id === Number(campaignId);
      });

      if (!!conversation) {
        return conversation.conversation.conversationId;
      } else {
        return await createConversation(
          { context: 'CAMPAIGN', contextualObjectId: Number(campaignId) },
          Number(userId)
        );
      }
    } else if (pathname === ROUTE_MESSENGER_SUPPORT) {
      const conversation = conversations.find(({ conversation }) => conversation.context === 'SUPPORT');

      if (!!conversation) {
        return conversation.conversation.conversationId;
      } else {
        return await createConversation({ context: 'SUPPORT' }, null);
      }
    } else if (!!conversationId) {
      await fetchConversation(Number(conversationId));
      return Number(conversationId);
    }
  };

  const addInitialMessage = (conversationId: number) => {
    if (!!state?.message) {
      const tmpMessageId = generateTmpMessageId();
      setTimeout(() => addTextMessage({ tmpMessageId, content: state.message.trim(), conversationId }), 500);
    }
  };

  return { setConversationId, conversationMessages, selectedConversation, currentConversations };
};
